'use client';

import Link from '@/components/Link';
import {motion} from 'motion/react';
import {ArrowRight, Terminal} from 'lucide-react';
import {WHATSAPP_LINK} from '@/lib/landing/constants';
import {heroContent} from '@/lib/landing/content';
import {SectionContainer} from './shared';

const FADE_EASE = [0.16, 1, 0.3, 1] as const;

export function HeroSection() {
  const entrance = (delay: number) => ({
    initial: {opacity: 0, y: 20},
    animate: {opacity: 1, y: 0},
    transition: {duration: 0.7, delay, ease: FADE_EASE},
  });

  const imageEntrance = {
    initial: {opacity: 0, scale: 0.95},
    animate: {opacity: 1, scale: 1},
    transition: {duration: 0.9, delay: 0.4, ease: FADE_EASE},
  };

  return (
    <section
      aria-labelledby="hero-heading"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 lg:pt-44 lg:pb-32 bg-white overflow-hidden border-b border-kobra-border lg:min-h-[720px]"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/45 pointer-events-none"
      />

      <img
        src="/image/fondo-hero.png"
        alt=""
        aria-hidden="true"
        className="absolute top-1/2 right-0 -translate-y-1/2 w-[min(920px,58vw)] h-auto opacity-[0.62] object-contain object-right pointer-events-none select-none hidden lg:block"
      />

      <img
        src="/image/fondo-hero.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-x-0 top-24 w-full h-auto max-h-[320px] opacity-[0.45] object-cover object-[center_top] pointer-events-none select-none lg:hidden"
      />

      <div
        aria-hidden="true"
        className="hidden lg:block absolute top-1/2 left-[28%] -translate-y-1/2 w-[min(760px,52vw)] h-[min(520px,38vw)] bg-white/50 blur-3xl rounded-full pointer-events-none"
      />

      <motion.img
        {...imageEntrance}
        src="/image/hero.png"
        alt=""
        aria-hidden="true"
        width={1672}
        height={941}
        className="hidden lg:block absolute top-1/2 left-[34%] -translate-y-[45%] w-[min(980px,68vw)] h-auto pointer-events-none select-none drop-shadow-[0_8px_32px_rgba(15,23,42,0.08)]"
      />

      <SectionContainer className="relative z-10">
        <div className="max-w-xl lg:max-w-2xl space-y-8">
          <motion.span
            {...entrance(0)}
            className="inline-flex items-center rounded-full border border-kobra-accent/30 bg-kobra-accent-soft px-3 py-1 text-sm text-kobra-accent font-medium"
          >
            {heroContent.badge}
          </motion.span>

          <div className="space-y-5">
            <motion.h1
              {...entrance(0.1)}
              id="hero-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-kobra-text-dark leading-[1.1] tracking-tight"
            >
              {heroContent.title}
            </motion.h1>
            <motion.p
              {...entrance(0.2)}
              className="text-lg text-kobra-text-muted leading-relaxed max-w-xl"
            >
              {heroContent.description}
            </motion.p>
          </div>

          <motion.div {...entrance(0.3)} className="flex flex-col sm:flex-row gap-4">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="bg-kobra-accent hover:bg-kobra-accent-hover text-white px-6 py-3.5 rounded-full font-medium text-base transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            >
              Solicitar demo
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </a>
            <Link
              href="#docs"
              className="bg-white hover:bg-kobra-bg border border-kobra-border text-kobra-text-dark px-6 py-3.5 rounded-full font-medium text-base transition-colors flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kobra-accent"
            >
              <Terminal className="w-5 h-5 text-kobra-text-muted" aria-hidden="true" />
              Explorar la documentación
            </Link>
          </motion.div>
        </div>

        <motion.div {...entrance(0.4)} className="lg:hidden mt-10 -mx-2 sm:mx-0">
          <img
            src="/image/hero.png"
            alt="Panel de Kobra mostrando cobros, conciliación y dispersiones conectados"
            className="w-full h-auto"
            width={1672}
            height={941}
          />
        </motion.div>
      </SectionContainer>
    </section>
  );
}
